/**
 * Enable predicates for the three browser telemetry pipelines booted by
 * `instrumentation-client.ts`: Mixpanel, Sentry and OTel (`lib/otel/tracer.ts`,
 * `lib/otel/logger.ts`). Pure functions over `TelemetryGateInputs` so every
 * branch is unit-testable; `readTelemetryGateInputs()` reads the real env.
 *
 * Mock mode (`lib/mockMode.ts`) turns all three off: a session on MSW fixtures
 * with a fake user must never reach Sentry, Mixpanel or Kibana. Sample rates
 * are untouched (see `.claude/rules/web/analytics.md`); only init is gated.
 */
import { isLocalhost } from "@/utils/isLocalhost";

import { isMockModeActive } from "./mockMode";

export interface TelemetryGateInputs {
  /** `resolveTelemetryEnvironment()` output: production / uat / NODE_ENV. */
  environment: string;
  isLocalhost: boolean;
  /** `NEXT_PUBLIC_IS_TEST_ENV` (E2E / Storybook builds). */
  isTestEnv: boolean;
  isMockMode: boolean;
  /** `NEXT_PUBLIC_ENABLE_MIXPANEL_LOCAL`. */
  enableMixpanelLocal: boolean;
  /** `NEXT_PUBLIC_DISABLE_MIXPANEL_UAT`. */
  disableMixpanelUat: boolean;
  /** `NEXT_PUBLIC_OTEL_DISABLED`. */
  otelDisabled: boolean;
}

export function resolveTelemetryEnvironment(
  backendEnv: string | undefined,
  nodeEnv: string | undefined
): string {
  if (backendEnv === "prd") return "production";
  if (backendEnv === "uat") return "uat";
  return nodeEnv || "development";
}

const isDeployed = (environment: string): boolean =>
  environment === "production" || environment === "uat";

/**
 * Always on in production; on in deployed UAT unless disabled; off on
 * localhost unless explicitly enabled; off in test builds and mock mode.
 */
export function shouldEnableMixpanel(inputs: TelemetryGateInputs): boolean {
  if (inputs.isMockMode) return false;
  if (inputs.isTestEnv) return false;
  if (inputs.isLocalhost) return inputs.enableMixpanelLocal;
  if (inputs.environment === "production") return true;
  if (inputs.environment === "uat") return !inputs.disableMixpanelUat;
  return false;
}

/** On in deployed production and UAT; off on localhost, test builds, mock mode. */
export function shouldEnableSentry(inputs: TelemetryGateInputs): boolean {
  if (inputs.isMockMode) return false;
  return (
    isDeployed(inputs.environment) && !inputs.isLocalhost && !inputs.isTestEnv
  );
}

/** As Sentry, plus the `NEXT_PUBLIC_OTEL_DISABLED` per-env kill switch. */
export function shouldEnableOtel(inputs: TelemetryGateInputs): boolean {
  if (inputs.isMockMode) return false;
  return shouldEnableSentry(inputs) && !inputs.otelDisabled;
}

export interface ClientTelemetry {
  /** `resolveTelemetryEnvironment()` output — Sentry/OTel environment tag. */
  environment: string;
  mixpanel: boolean;
  sentry: boolean;
  otel: boolean;
}

/**
 * The one decision every browser telemetry boot site reads
 * (`instrumentation-client.ts`, `lib/otel/tracer.ts`, `lib/otel/logger.ts`),
 * so "mock mode turns all telemetry off" is one tested function rather than
 * three call sites that could drift.
 */
export function resolveClientTelemetry(
  inputs: TelemetryGateInputs = readTelemetryGateInputs()
): ClientTelemetry {
  return {
    environment: inputs.environment,
    mixpanel: shouldEnableMixpanel(inputs),
    sentry: shouldEnableSentry(inputs),
    otel: shouldEnableOtel(inputs),
  };
}

/**
 * `window.localStorage` itself can throw (SecurityError when storage is
 * blocked). This runs in the telemetry boot path, so a throw here would take
 * Sentry down with it: treat unreadable storage as "no stored flag".
 */
function browserStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads the gate inputs from the build-time env, the hostname and the stored
 * mock-mode flag. Mock mode is only read from storage in the browser; on the
 * server only the build-time `NEXT_PUBLIC_MOCK_MODE` can turn it on.
 */
export function readTelemetryGateInputs(
  storage: Storage | null = browserStorage()
): TelemetryGateInputs {
  return {
    environment: resolveTelemetryEnvironment(
      process.env.NEXT_PUBLIC_BACKEND_ENVIRONMENT,
      process.env.NODE_ENV
    ),
    isLocalhost: isLocalhost(),
    isTestEnv: process.env.NEXT_PUBLIC_IS_TEST_ENV === "true",
    // `null` (not `undefined`) for "no storage": `isMockModeActive(undefined)`
    // would fall back to its own default storage. isMockModeActive guards the
    // `getItem` read itself (blocked storage reads as "off"), so a throwing
    // store can't take the telemetry boot path down.
    isMockMode: isMockModeActive(storage),
    enableMixpanelLocal:
      process.env.NEXT_PUBLIC_ENABLE_MIXPANEL_LOCAL === "true",
    disableMixpanelUat: process.env.NEXT_PUBLIC_DISABLE_MIXPANEL_UAT === "true",
    otelDisabled: process.env.NEXT_PUBLIC_OTEL_DISABLED === "true",
  };
}
